import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useFacility } from "@/context/FacilityContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Plus, Loader2, Trash2, TrendingUp, TrendingDown, Wallet, IndianRupee, Clock, Download,
} from "lucide-react";
import { toast } from "sonner";

const MODES = ["Cash", "UPI", "Card", "Cheque", "Bank Transfer"];
const money = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

function SummaryCard({ label, value, icon: Icon, tone, testId }) {
  const tones = {
    green: "text-emerald-700 bg-emerald-50 border-emerald-100",
    red: "text-red-700 bg-red-50 border-red-100",
    blue: "text-blue-700 bg-blue-50 border-blue-100",
  };
  return (
    <div className={`rounded-md border p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide opacity-80">{label}</span>
        <Icon className="h-4 w-4 opacity-70" />
      </div>
      <div className="mt-2 text-2xl font-semibold flex items-center"><IndianRupee className="h-4 w-4" />{money(value)}</div>
    </div>
  );
}

// ---------- Types management (income or expense) ----------
function TypesPanel({ kind }) {
  const { activeId, has } = useFacility();
  const canManage = has("finance:manage_settings");
  const [rows, setRows] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!activeId) return;
    setRows(null);
    api.get(`/finance/types?kind=${kind}&facility_id=${activeId}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [activeId, kind]);
  useEffect(load, [load]);

  const add = async () => {
    if (!name.trim()) { toast.error("Type name is required"); return; }
    setSaving(true);
    try {
      await api.post("/finance/types", { kind, name: name.trim(), facility_id: activeId });
      toast.success(`Added "${name.trim()}"`);
      setName("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const remove = async (r) => {
    try { await api.delete(`/finance/types/${r.id}`); toast.success("Type removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const label = kind === "income" ? "Income" : "Expense";
  return (
    <div data-testid={`${kind}-types-panel`}>
      {canManage && (
        <div className="bg-white border border-slate-200 rounded-md p-5 mb-6" data-testid={`add-${kind}-type-card`}>
          <div className="flex items-center gap-2 text-slate-800 font-semibold mb-4"><Plus className="h-5 w-5 text-blue-600" /> Add {label} Type</div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
            <div className="space-y-1.5">
              <Label className="text-xs">{label} type name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                placeholder={kind === "income" ? "e.g. Consultation, Pharmacy Sales" : "e.g. Salaries, Utilities, Supplies"}
                data-testid={`${kind}-type-name-input`} />
            </div>
            <Button onClick={add} disabled={saving} className="bg-blue-700 hover:bg-blue-800 h-9" data-testid={`${kind}-type-save-button`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
            </Button>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">These headings appear when staff record {label.toLowerCase()} entries.</p>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {rows === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>{label} Type</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`${kind}-type-row-${r.id}`}>
                  <TableCell className="font-medium text-slate-800">{r.name}</TableCell>
                  <TableCell className="text-right">
                    {canManage && <button onClick={() => remove(r)} className="text-slate-300 hover:text-red-500" data-testid={`${kind}-type-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-slate-400 py-12">No {label.toLowerCase()} types yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

// ---------- Transactions (record + list) ----------
function TransactionsPanel({ onChanged }) {
  const { activeId, has } = useFacility();
  const canRecord = has("finance:create");
  const [types, setTypes] = useState({ income: [], expense: [] });
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [saving, setSaving] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ kind: "income", type_id: "", date: today, description: "", amount: "", mode: "Cash" });

  const loadTypes = useCallback(() => {
    if (!activeId) return;
    Promise.all([
      api.get(`/finance/types?kind=income&facility_id=${activeId}`),
      api.get(`/finance/types?kind=expense&facility_id=${activeId}`),
    ]).then(([i, e]) => setTypes({ income: i.data, expense: e.data })).catch(() => {});
  }, [activeId]);
  const loadEntries = useCallback(() => {
    if (!activeId) return;
    setRows(null);
    api.get(`/finance/entries?facility_id=${activeId}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [activeId]);
  useEffect(() => { loadTypes(); loadEntries(); }, [loadTypes, loadEntries]);

  const curTypes = types[form.kind] || [];
  const record = async () => {
    if (!form.type_id) { toast.error("Select a type"); return; }
    if (form.amount === "" || Number(form.amount) <= 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      const t = curTypes.find((x) => x.id === form.type_id);
      await api.post("/finance/entries", {
        kind: form.kind, type_id: form.type_id, type_name: t?.name,
        date: form.date, description: form.description, amount: Number(form.amount),
        mode: form.mode, facility_id: activeId,
      });
      toast.success(`${form.kind === "income" ? "Income" : "Expense"} recorded`);
      setForm({ ...form, type_id: "", description: "", amount: "" });
      loadEntries();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const visible = (rows || []).filter((r) => filter === "all" || r.kind === filter);

  return (
    <div data-testid="finance-transactions-panel">
      {canRecord && (
        <div className="bg-white border border-slate-200 rounded-md p-5 mb-6" data-testid="record-entry-card">
          <div className="flex items-center gap-2 text-slate-800 font-semibold mb-4"><Plus className="h-5 w-5 text-blue-600" /> Record Transaction</div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
            <div className="space-y-1.5">
              <Label className="text-xs">Kind</Label>
              <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white"
                value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value, type_id: "" })} data-testid="entry-kind-select">
                <option value="income">Income</option>
                <option value="expense">Expense</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Type</Label>
              <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white"
                value={form.type_id} onChange={(e) => setForm({ ...form, type_id: e.target.value })} data-testid="entry-type-select">
                <option value="">Select type…</option>
                {curTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="entry-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Amount (₹)</Label>
              <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" data-testid="entry-amount-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Payment mode</Label>
              <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white"
                value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} data-testid="entry-mode-select">
                {MODES.map((m) => <option key={m}>{m}</option>)}
              </select>
            </div>
            <Button onClick={record} disabled={saving} className="bg-blue-700 hover:bg-blue-800 h-9" data-testid="entry-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Record"}
            </Button>
          </div>
          <div className="mt-3">
            <Label className="text-xs">Description (optional)</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Notes / reference" data-testid="entry-description-input" />
          </div>
          {curTypes.length === 0 && <p className="text-[11px] text-amber-600 mt-2">No {form.kind} types yet — add them in the {form.kind === "income" ? "Income" : "Expense"} Types tab first.</p>}
        </div>
      )}

      <div className="flex items-center gap-2 mb-3" data-testid="entry-filter">
        {["all", "income", "expense"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`entry-filter-${f}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${filter === f ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {f === "all" ? "All" : f === "income" ? "Income" : "Expense"}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {rows === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Date</TableHead><TableHead>Kind</TableHead><TableHead>Type</TableHead>
              <TableHead>Description</TableHead><TableHead>Mode</TableHead><TableHead className="text-right">Amount (₹)</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {visible.map((r) => (
                <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`entry-row-${r.id}`}>
                  <TableCell className="text-slate-600">{r.date}</TableCell>
                  <TableCell>
                    <span className={`text-xs rounded px-2 py-0.5 ${r.kind === "income" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{r.kind}</span>
                  </TableCell>
                  <TableCell className="font-medium text-slate-800">{r.type_name || "—"}{r.auto && <span className="ml-1 text-[10px] text-blue-500">auto</span>}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{r.description || "—"}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{r.mode}</TableCell>
                  <TableCell className={`text-right font-semibold ${r.kind === "income" ? "text-emerald-700" : "text-red-700"}`}>{r.kind === "expense" ? "-" : ""}{money(r.amount)}</TableCell>
                </TableRow>
              ))}
              {visible.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-12">No transactions yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function ReportsPanel() {
  const { activeId } = useFacility();
  const [rows, setRows] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [kind, setKind] = useState("all");
  const [typeName, setTypeName] = useState("all");

  const load = useCallback(() => {
    if (!activeId) return;
    setRows(null);
    api.get(`/finance/entries?facility_id=${activeId}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [activeId]);
  useEffect(load, [load]);

  const preset = (p) => {
    const t = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (p === "today") { setFrom(iso(t)); setTo(iso(t)); }
    else if (p === "month") { setFrom(iso(t).slice(0, 8) + "01"); setTo(iso(t)); }
    else if (p === "30") { const d = new Date(t); d.setDate(d.getDate() - 29); setFrom(iso(d)); setTo(iso(t)); }
  };

  const all = rows || [];
  const typeNames = [...new Set(all.filter((e) => kind === "all" || e.kind === kind).map((e) => e.type_name).filter(Boolean))].sort();
  const filtered = all.filter((e) => {
    const d = (e.date || "").slice(0, 10);
    if (from && d < from) return false;
    if (to && d > to) return false;
    if (kind !== "all" && e.kind !== kind) return false;
    if (typeName !== "all" && e.type_name !== typeName) return false;
    return true;
  });
  const totIncome = filtered.filter((e) => e.kind === "income").reduce((s, e) => s + Number(e.amount || 0), 0);
  const totExpense = filtered.filter((e) => e.kind === "expense").reduce((s, e) => s + Number(e.amount || 0), 0);

  const byDay = filtered.reduce((acc, e) => { (acc[e.date] = acc[e.date] || []).push(e); return acc; }, {});
  const days = Object.keys(byDay).sort().reverse();

  const exportCsv = () => {
    const header = ["Date", "Kind", "Type", "Description", "Mode", "Amount"];
    const esc = (c) => `"${String(c ?? "").replace(/"/g, '""')}"`;
    const lines = filtered
      .slice().sort((a, b) => (a.date < b.date ? 1 : -1))
      .map((e) => [e.date, e.kind, e.type_name || "", e.description || "", e.mode || "", e.amount].map(esc).join(","));
    const csv = [header.map(esc).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `finance-report_${from}_to_${to}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="finance-reports-panel">
      <div className="bg-white border border-slate-200 rounded-md p-5 mb-5" data-testid="report-filters">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="report-from" /></div>
          <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="report-to" /></div>
          <div className="space-y-1"><Label className="text-xs">Kind</Label>
            <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={kind} onChange={(e) => { setKind(e.target.value); setTypeName("all"); }} data-testid="report-kind">
              <option value="all">All</option><option value="income">Income</option><option value="expense">Expense</option>
            </select></div>
          <div className="space-y-1"><Label className="text-xs">Type</Label>
            <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white min-w-[10rem]" value={typeName} onChange={(e) => setTypeName(e.target.value)} data-testid="report-type">
              <option value="all">All types</option>
              {typeNames.map((t) => <option key={t} value={t}>{t}</option>)}
            </select></div>
          <div className="flex gap-1.5">
            {[["today", "Today"], ["month", "This month"], ["30", "Last 30d"]].map(([p, l]) => (
              <button key={p} onClick={() => preset(p)} className="text-xs px-2.5 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50" data-testid={`report-preset-${p}`}>{l}</button>
            ))}
          </div>
          <div className="ml-auto">
            <Button onClick={exportCsv} disabled={filtered.length === 0} className="bg-slate-900 hover:bg-slate-800 h-9" data-testid="report-export"><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5" data-testid="report-summary">
        <SummaryCard label="Income (period)" value={totIncome} icon={TrendingUp} tone="green" testId="report-sum-income" />
        <SummaryCard label="Expense (period)" value={totExpense} icon={TrendingDown} tone="red" testId="report-sum-expense" />
        <SummaryCard label="Net (period)" value={totIncome - totExpense} icon={Wallet} tone="blue" testId="report-sum-net" />
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {rows === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : days.length === 0 ? (
          <div className="text-center text-slate-400 py-12">No transactions in this period.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {days.map((d) => {
              const list = byDay[d];
              const inc = list.filter((e) => e.kind === "income").reduce((s, e) => s + Number(e.amount || 0), 0);
              const exp = list.filter((e) => e.kind === "expense").reduce((s, e) => s + Number(e.amount || 0), 0);
              return (
                <div key={d} data-testid={`report-day-${d}`}>
                  <div className="bg-slate-50 px-4 py-2 flex justify-between text-xs font-semibold text-slate-600">
                    <span>{new Date(d).toLocaleDateString([], { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}</span>
                    <span className="flex gap-4"><span className="text-emerald-700">+{money(inc)}</span><span className="text-red-700">-{money(exp)}</span></span>
                  </div>
                  <Table>
                    <TableBody>
                      {list.map((e) => (
                        <TableRow key={e.id} data-testid={`report-row-${e.id}`}>
                          <TableCell className="w-24"><span className={`text-xs rounded px-2 py-0.5 ${e.kind === "income" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{e.kind}</span></TableCell>
                          <TableCell className="font-medium text-slate-800">{e.type_name || "—"}{e.auto && <span className="ml-1 text-[10px] text-blue-500">auto</span>}</TableCell>
                          <TableCell className="text-sm text-slate-500">{e.description || "—"}</TableCell>
                          <TableCell className="text-sm text-slate-500 w-28">{e.mode}</TableCell>
                          <TableCell className={`text-right font-semibold w-32 ${e.kind === "income" ? "text-emerald-700" : "text-red-700"}`}>{e.kind === "expense" ? "-" : ""}{money(e.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function PendingPanel() {
  const { activeId } = useFacility();
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    if (!activeId) return;
    setData(null);
    api.get(`/finance/pending?facility_id=${activeId}`).then((r) => setData(r.data)).catch(() => setData({ items: [], total_pending: 0 }));
  }, [activeId]);
  useEffect(load, [load]);

  return (
    <div data-testid="finance-pending-panel">
      <div className="rounded-md border border-amber-100 bg-amber-50 p-4 mb-4 flex items-center justify-between" data-testid="pending-summary">
        <span className="text-sm font-medium text-amber-800 flex items-center gap-2"><Clock className="h-4 w-4" /> Outstanding pending bills</span>
        <span className="text-xl font-semibold text-amber-800 flex items-center"><IndianRupee className="h-4 w-4" />{money(data?.total_pending)}</span>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {data === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Invoice</TableHead><TableHead>Patient</TableHead><TableHead>IPD No</TableHead>
              <TableHead className="text-right">Total</TableHead><TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Balance</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.items.map((r) => (
                <TableRow key={r.admission_id} className="hover:bg-slate-50" data-testid={`pending-row-${r.admission_id}`}>
                  <TableCell className="font-mono text-xs text-blue-700">{r.invoice_no}</TableCell>
                  <TableCell className="font-medium text-slate-800">{r.patient_name}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{r.ipd_no}</TableCell>
                  <TableCell className="text-right">{money(r.total)}</TableCell>
                  <TableCell className="text-right text-emerald-700">{money(r.paid)}</TableCell>
                  <TableCell className="text-right font-semibold text-amber-700">{money(r.balance)}</TableCell>
                </TableRow>
              ))}
              {data.items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-12">No pending bills. All invoices are settled.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

export default function Finance() {
  const { activeId, activeFacility, has } = useFacility();
  const [sum, setSum] = useState(null);

  const loadSum = useCallback(() => {
    if (!activeId) return;
    api.get(`/finance/summary?facility_id=${activeId}`).then((r) => setSum(r.data)).catch(() => setSum(null));
  }, [activeId]);
  useEffect(loadSum, [loadSum]);

  if (!has("finance:view")) return <Navigate to="/app" replace />;

  return (
    <div data-testid="finance-page">
      <PageHeader title="Finance"
        subtitle={activeFacility ? `Income & expense accounting for ${activeFacility.name}.` : "Select a facility to manage finance."} />

      <Tabs defaultValue="overview" className="w-full">
        <TabsList data-testid="finance-tabs">
          <TabsTrigger value="overview" data-testid="finance-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="income-types" data-testid="finance-tab-income-types">Income Types</TabsTrigger>
          <TabsTrigger value="expense-types" data-testid="finance-tab-expense-types">Expense Types</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="finance-tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="reports" data-testid="finance-tab-reports">Reports</TabsTrigger>
          <TabsTrigger value="pending" data-testid="finance-tab-pending">Pending Bills</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="finance-summary-month">
            <SummaryCard label="Income (this month)" value={sum?.month_income} icon={TrendingUp} tone="green" testId="sum-month-income" />
            <SummaryCard label="Expense (this month)" value={sum?.month_expense} icon={TrendingDown} tone="red" testId="sum-month-expense" />
            <SummaryCard label="Net (this month)" value={sum?.month_net} icon={Wallet} tone="blue" testId="sum-month-net" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="finance-summary-total">
            <SummaryCard label="Total Income" value={sum?.income} icon={TrendingUp} tone="green" testId="sum-total-income" />
            <SummaryCard label="Total Expense" value={sum?.expense} icon={TrendingDown} tone="red" testId="sum-total-expense" />
            <SummaryCard label="Net Balance" value={sum?.net} icon={Wallet} tone="blue" testId="sum-total-net" />
          </div>
        </TabsContent>

        <TabsContent value="income-types" className="mt-5"><TypesPanel kind="income" /></TabsContent>
        <TabsContent value="expense-types" className="mt-5"><TypesPanel kind="expense" /></TabsContent>
        <TabsContent value="transactions" className="mt-5"><TransactionsPanel onChanged={loadSum} /></TabsContent>
        <TabsContent value="reports" className="mt-5"><ReportsPanel /></TabsContent>
        <TabsContent value="pending" className="mt-5"><PendingPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
